/**
 * The five examples the landing page cycles through — what they SAY, and where their sites live.
 *
 * ⚠️ THE RESULT IS COMMITTED RATHER THAN GENERATED, AND THAT IS THE POINT OF THESE TWO FILES. Pressing
 * the button on an example used to spend model tokens rewriting, in about half a minute, five
 * sentences that never change — for a visitor who has not typed anything and is deciding whether this
 * product is worth typing into. Every one of those runs cost money, took long enough to lose people,
 * and could come back different (or fail) on the one screen the product is being judged on.
 *
 * ⚠️ SO AN EXAMPLE IS NOT A PROMPT, IT IS A DEMONSTRATION. The description, the address the model gave
 * it, and the document the model produced are ONE declaration — this file and `sample-sites.ts` — so
 * the landing page and the editor cannot drift apart. The moment a visitor edits the words it stops
 * being an example and generation is real again (see `Landing`).
 *
 * ⚠️ AND THE DOCUMENTS ARE BEHIND A DYNAMIC `import()`, WHICH IS WHY THIS FILE IS SHORT. Five whole
 * websites are ~30 kB of copy that the marketing page never renders; what it needs is five sentences
 * and five addresses. Same split, same reason, as the placeholder gallery in `@ksa/ui` — see
 * `loadSampleSite` below and CLAUDE.md §5.11.
 *
 * ⚠️ THE PICTURES ARE STILL PLACEHOLDERS, DELIBERATELY. CLAUDE.md §5.10: a generated site is not
 * publishable, because inventing an image key means a broken picture on a real business's homepage.
 * Committing the copy changes nothing about that — `readyToPublish` says the same thing about a
 * sample as it says about a generated site, and it must, or the demonstration would be lying about
 * the product.
 *
 * ⚠️ AND THE ARABIC IS WRITTEN, NOT TRANSLATED. Same rule as `demoSite()`: this is the sample a Saudi
 * visitor judges the product by, and copy that reads as machine-translated says the product was built
 * for somebody else.
 */

import type { Site } from '../types.ts'

export type Sample = {
  /** Stable, and it is in the URL of the demonstration — so it may not be renamed casually. */
  id: string
  /**
   * What the box on the landing page says.
   *
   * ⚠️ BOTH LANGUAGES, NOT A `Text`. `Text` allows `{ lit }` — one string used in both — and a
   * description that is the same in English and Arabic is not an example of anything. The two are
   * separately written, and `matchSample` compares against each of them.
   */
  description: { en: string; ar: string }
  /**
   * ⚠️ THE ADDRESS THE MODEL ACTUALLY GAVE IT, generated once by `POST /api/ai/slug` and committed.
   * The landing page cannot ask for one: that route needs a session, and an unauthenticated endpoint
   * that spends model tokens per keystroke is a bill anybody can run up.
   */
  handle: string
}

/**
 * Whether some words are one of the examples, rather than something a visitor wrote.
 *
 * ⚠️ WHITESPACE-INSENSITIVE AND NOTHING ELSE. Not "similar to", not "starts with": a visitor who
 * changed one word of an example is describing a different business, and showing them the flower
 * shop would be the product ignoring what they typed. The only forgiveness is for the wrapping and
 * trimming a textarea does on its own.
 */
export function matchSample(description: string): Sample | undefined {
  const wanted = squash(description)
  if (!wanted) return undefined
  return SAMPLES.find((sample) => squash(sample.description.en) === wanted || squash(sample.description.ar) === wanted)
}

export function sampleFor(id: string): Sample | undefined {
  return SAMPLES.find((sample) => sample.id === id)
}

function squash(value: string): string {
  return value.replace(/\s+/g, ' ').trim()
}

/**
 * The site one of the examples produces, fetched on demand.
 *
 * ⚠️ A DYNAMIC IMPORT, AND THE ABSENCE OF A STATIC ONE IS THE LOAD-BEARING PART. The documents are
 * ~30 kB of copy; a static import puts every word of five websites into the chunk that paints the
 * marketing page, which renders none of them. There is a test on the absence, because restoring one
 * has no visible symptom at all.
 *
 * ⚠️ IT RESOLVES TO `null` FOR AN UNKNOWN ID *AND* FOR A CHUNK THAT WOULD NOT LOAD, and the caller
 * shows the same thing for both: there is nothing here. A demonstration that fails to arrive is a
 * screen that says so, not a broken editor.
 */
export async function loadSampleSite(id: string): Promise<Site | null> {
  if (!sampleFor(id)) return null
  const module = await import('./sample-sites.ts').catch(() => null)
  return module?.SITES[id]?.() ?? null
}

/**
 * ⚠️ EACH ONE NAMES A REAL RIYADH DISTRICT, because "a restaurant in Riyadh" is a demo and "a
 * restaurant in Al Malaz" is somebody's actual business. It is also what makes the result worth
 * looking at: the copy is about a neighbourhood rather than about a city of eight million.
 */
export const SAMPLES: Sample[] = [
  {
    id: 'malaz-four-dishes',
    handle: 'malaz-four-dishes',
    description: {
      en: 'A small restaurant in Al Malaz. Four dishes a night, written on the board by six. We deliver in the north of the city.',
      ar: 'مطعم صغير في الملز. أربعة أطباق كل ليلة، تُكتب على اللوحة قبل السادسة. نوصّل في شمال المدينة.',
    },
  },
  {
    id: 'olaya-coffee-roastery',
    handle: 'olaya-coffee-roastery',
    description: {
      en: 'We roast coffee in Al Olaya and sell it online and in one shop on Tahlia Street. Single origin, roasted every Tuesday.',
      ar: 'نحمّص القهوة في العليا ونبيعها أونلاين وفي محل واحد بشارع التحلية. أصل واحد، تحميص كل ثلاثاء.',
    },
  },
  {
    id: 'al-narjis-dental',
    handle: 'al-narjis-dental',
    description: {
      en: 'A family dental clinic in Al Narjis. Cleanings, fillings and braces, six days a week, and we see children.',
      ar: 'عيادة أسنان عائلية في النرجس. تنظيف وحشوات وتقويم، ستة أيام في الأسبوع، ونستقبل الأطفال.',
    },
  },
  {
    id: 'ghirnatah-phone-repair',
    handle: 'ghirnatah-phone-repair',
    description: {
      en: 'We fix and sell phone accessories in Ghirnatah. Screens, cases and chargers, and most repairs are done the same day.',
      ar: 'نصلح ونبيع إكسسوارات الجوال في غرناطة. شاشات وأغطية وشواحن، ومعظم الإصلاحات تُنجز في نفس اليوم.',
    },
  },
  {
    id: 'safarat-flowers',
    handle: 'safarat-flowers',
    description: {
      en: 'A flower shop in Al Safarat. Arrangements for weddings and offices, delivered anywhere in Riyadh before noon.',
      ar: 'محل ورد في السفارات. تنسيق للأعراس والمكاتب، مع توصيل في أي مكان بالرياض قبل الظهر.',
    },
  },
]
